import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ShoppingCartOutlined, ShoppingOutlined } from "@ant-design/icons";
import { Button, Col, Row, Typography, Card, message } from "antd";

import ContentTitle from "../../components/ContentTitle/ContentTitle";
import {
  selectCartItems,
  selectIsCartLoading,
} from "../../redux-toolkit/cart/cart-selector";
import { fetchCart } from "../../redux-toolkit/cart/cart-thunks";
import {
  calculateCartPrice,
  removePerfumeById,
  resetCartState,
  setCartItemsCount,
} from "../../redux-toolkit/cart/cart-slice";
import CartItem from "./CartItem/CartItem";
import Spinner from "../../components/Spinner/Spinner";
import "./Cart.css";
import { ORDER } from "../../constants/routeConstants";

const Cart: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const perfumes = useSelector(selectCartItems);
  const isCartLoading = useSelector(selectIsCartLoading);

  const [perfumeInCart, setPerfumeInCart] = useState<Map<number, number>>(new Map());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Load cart + fix invalid entries
  useEffect(() => {
    window.scrollTo(0, 0);

    let stored = localStorage.getItem("perfumes");
    if (!stored || stored === "null" || stored === "undefined") {
      stored = "[]";
    }

    let parsed: [number, number][] = [];
    try {
      const temp = JSON.parse(stored);
      parsed = Array.isArray(temp) ? temp : [];
    } catch (e) {
      parsed = [];
      localStorage.setItem("perfumes", "[]");
    }

    const cleaned: [number, number][] = parsed.filter(
      (item) =>
        Array.isArray(item) &&
        typeof item[0] === "number" &&
        typeof item[1] === "number" &&
        !isNaN(item[0])
    );

    if (cleaned.length !== parsed.length) {
      localStorage.setItem("perfumes", JSON.stringify(cleaned));
    }

    const perfumesFromLocalStorage = new Map<number, number>(cleaned);

    const ids: number[] = Array.from(perfumesFromLocalStorage.keys());
    dispatch(fetchCart(ids));
    dispatch(setCartItemsCount(ids.length));

    perfumesFromLocalStorage.forEach((qty: number, id: number) => {
      setPerfumeInCart((prev) => new Map(prev).set(id, qty));
    });
  }, [dispatch]);

  // ❌ Delete item
  const deleteFromCart = (perfumeId: number): void => {
    const updated = new Map(perfumeInCart);
    updated.delete(perfumeId);

    if (updated.size === 0) {
      localStorage.removeItem("perfumes");
    } else {
      localStorage.setItem("perfumes", JSON.stringify(Array.from(updated.entries())));
    }

    setPerfumeInCart(updated);
    dispatch(removePerfumeById(perfumeId));
    dispatch(setCartItemsCount(updated.size));
  };

  // 🔄 Quantity change + Min/Max validation + calculate price
  const onChangePerfumeItemCount = (perfumeId: number, qty: number): void => {
    const perfume = perfumes.find((p) => p.id === perfumeId);
    if (!perfume) return;

    const minQty = perfume.minQuantity || 1;
    const maxQty = perfume.maxQuantity || 999999; // NEW maxQuantity

    // 🚫 Max quantity check
    if (qty > maxQty) {
      message.error(`Maximum quantity allowed is ${maxQty}`);
      qty = maxQty;
    }

    // 🚫 Min quantity check
    if (qty < minQty) {
      message.warning(`Minimum order for this product is ${minQty}`);
      qty = minQty;
    }

    const updated = new Map(perfumeInCart);
    updated.set(perfumeId, qty);

    setPerfumeInCart(updated);
    localStorage.setItem("perfumes", JSON.stringify(Array.from(updated.entries())));

    const updatedPerfumes = perfumes.map((p) => ({
      ...p,
      quantity: updated.get(p.id) || p.minQuantity || 1,
    }));

    dispatch(calculateCartPrice(updatedPerfumes));
  };


 const subtotal = perfumes.reduce((total, p) => {
   const qty = perfumeInCart.get(p.id) || p.minQuantity || 1;
   return total + p.price * qty;
 }, 0);

 // Per-product shipping + fallback to 10%
const deliveryCharge = perfumes.reduce((total, p) => {
  const qty = perfumeInCart.get(p.id) || p.minQuantity || 1;
  const ship = p.shipPer;

  if (ship && ship > 0) {
    const charge = Math.floor((p.price * qty * ship) / 100);
    return total + Math.max(charge, 1); // ensure minimum ₹1
  }

  // apply default 10%
  const fallback = Math.floor((p.price * qty * 10) / 100);
  return total + Math.max(fallback, 1);
}, 0);


 const total = subtotal + deliveryCharge;


  const handleCheckout = () => {
    window.location.href = ORDER;
  };

  return (
    <div className="cart-container">
      {isCartLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="cart-header">
            <ContentTitle icon={<ShoppingCartOutlined />} title="My Cart" />
          </div>

          <Row gutter={[24, 24]} justify="center" className="cart-layout">
            {perfumes.length === 0 ? (
              <Col span={24}>
                <Typography.Title level={3} style={{ textAlign: "center" }}>
                  Your cart is empty
                </Typography.Title>
              </Col>
            ) : (
              <>
                {/* Cart Items */}
                <Col xs={24} md={16} lg={17}>
                  <Card bordered={false}>
                    {perfumes.map((p) => (
                      <CartItem
                        key={p.id}
                        perfume={p}
                        perfumeInCart={perfumeInCart.get(p.id) || p.minQuantity || 1}
                        onChangePerfumeItemCount={onChangePerfumeItemCount}
                        deleteFromCart={deleteFromCart}
                      />
                    ))}
                  </Card>
                </Col>

                {/* Summary */}
                <Col xs={24} md={8} lg={7}>
                  <Card bordered={false}>
                    <h3>Subtotal: ₹{subtotal.toLocaleString()}</h3>
                    <h3>Shipping Charges: ₹{deliveryCharge.toLocaleString()}</h3>
                    <h2>Total: ₹{total.toLocaleString()}</h2>

                    <Button
                      type="primary"
                      icon={<ShoppingOutlined />}
                      size="large"
                      onClick={handleCheckout}
                    >
                      Checkout
                    </Button>
                  </Card>
                </Col>
              </>
            )}
          </Row>

          {/* Mobile Bottom Bar */}
          {isMobile && perfumes.length > 0 && (
            <div className="cart-bottom-bar">
              <div className="total">₹{total.toLocaleString()}</div>
              <Button
                type="primary"
                className="place-order-btn"
                onClick={handleCheckout}
              >
                Place Order
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Cart;
